import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import React from "react";

/*
 * This is a manual version of BreadcrumbNavigation that uses the same styles and structure
 * but renders a provided list of BreadcrumbListItems instead of being router-based
 */

interface BreadcrumbListItem<T> {
    name: string;
    data: T;
}

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface BreadcrumbListProps<T> {
    breadcrumbs: BreadcrumbListItem<T>[];
    onBreadcrumbClick: (data: T) => void;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const MIN_BREADCRUMB_ELLIPSIS_LENGTH = 4;
const BASE_CSS_CLASS = "c-breadcrumb-navigation";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const BreadcrumbList = <T,>(props: BreadcrumbListProps<T>) => {
    const { breadcrumbs, onBreadcrumbClick } = props;

    const breadcrumbListItems = breadcrumbs.map(
        (breadcrumb: BreadcrumbListItem<T>, index: number) => {
            const isLastBreadcrumb = index === breadcrumbs.length - 1;
            const isSecondBreadcrumb = index === 1;
            const showEllipsis =
                breadcrumbs.length >= MIN_BREADCRUMB_ELLIPSIS_LENGTH &&
                isLastBreadcrumb;

            const className = buildCssClassname(
                isLastBreadcrumb,
                isSecondBreadcrumb
            );

            return (
                <React.Fragment key={index}>
                    {
                        // if
                        showEllipsis && (
                            <li>
                                <span className="c-breadcrumb -mobile-separator">
                                    <Icon
                                        type={Icons.ChevronRight}
                                        size={IconSizes.Base}
                                    />
                                    <span>...</span>
                                </span>
                            </li>
                        )
                    }
                    <li key={index}>
                        <button
                            className={className}
                            onClick={() => onBreadcrumbClick(breadcrumb.data)}>
                            {breadcrumb.name}
                            {!isLastBreadcrumb && (
                                <Icon
                                    type={Icons.ChevronRight}
                                    size={IconSizes.Base}
                                />
                            )}
                        </button>
                    </li>
                </React.Fragment>
            );
        }
    );
    return (
        <nav className={BASE_CSS_CLASS}>
            <ol>{breadcrumbListItems}</ol>
        </nav>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Private Functions
// -----------------------------------------------------------------------------------------

function buildCssClassname(
    lastBreadcrumb: boolean,
    secondBreadcrumb: boolean
): string {
    const classNames: string[] = ["c-breadcrumb"];

    if (lastBreadcrumb) {
        classNames.push("-last -unclickable");
    }

    if (secondBreadcrumb) {
        classNames.push("-second");
    }

    return classNames.join(" ");
}

// #endregion Private Functions

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default BreadcrumbList;

// #endregion Exports
